import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowUp } from "lucide-react";

const SECTIONS = [
  { id: "overview", label: "1. Overview" },
  { id: "quick-start", label: "2. Quick Start" },
  { id: "admin-console", label: "3. Admin Console" },
  { id: "daily", label: "4. Daily Ops" },
  { id: "weekly", label: "5. Weekly/Monthly" },
  { id: "therapists", label: "6. Therapist Flow" },
  { id: "customers", label: "7. Customer Flow" },
  { id: "money", label: "8. Money & Stripe" },
  { id: "email", label: "9. Email Setup" },
  { id: "glossary", label: "10. Glossary" },
  { id: "troubleshoot", label: "11. Troubleshooting" },
  { id: "env", label: "12. Production Env" },
];

const H2 = ({ id, children }) => (
  <h2 id={id} className="font-serif-display text-3xl tracking-tight mt-16 scroll-mt-24 first:mt-0 border-b border-[#E8E2D6] pb-3">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-serif-display text-xl tracking-tight mt-8">{children}</h3>
);
const P = ({ children }) => <p className="text-[15px] text-[#2C352D] leading-relaxed mt-3">{children}</p>;
const UL = ({ children }) => <ul className="mt-3 space-y-2 text-[15px] text-[#2C352D] list-disc pl-5">{children}</ul>;
const Table = ({ headers, rows }) => (
  <div className="mt-4 overflow-x-auto bg-white border border-[#E8E2D6] rounded-2xl">
    <table className="w-full text-sm">
      <thead className="bg-[#F9F7F3]">
        <tr>{headers.map((h, i) => <th key={i} className="text-left px-4 py-3 font-bold text-[#5F6C60] text-xs uppercase tracking-[0.15em]">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-[#E8E2D6]">
        {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="px-4 py-3 align-top text-[#2C352D]">{c}</td>)}</tr>)}
      </tbody>
    </table>
  </div>
);
const Code = ({ children }) => <code className="bg-[#F9F7F3] border border-[#E8E2D6] rounded px-1.5 py-0.5 text-[13px] text-[#0F766E] font-mono">{children}</code>;
const Note = ({ children }) => (
  <div className="mt-4 bg-[#0F766E]/8 border-l-4 border-[#0F766E] rounded-r-xl p-4 text-sm text-[#2C352D]">{children}</div>
);

export default function OwnerGuide() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 print:py-0">
      <div className="flex items-start justify-between gap-6 mb-10 print:mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Owner</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">Operations Manual</h1>
          <p className="text-[#5F6C60] mt-2">Everything you need to run Massage Savings — start to finish.</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Link to="/admin"><Button variant="outline" className="rounded-full">Back to Admin</Button></Link>
          <Button data-testid="print-guide" onClick={() => window.print()} className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print / Save as PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block sticky top-24 self-start h-fit print:hidden">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-3">Contents</div>
          <nav className="space-y-1.5">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="block text-sm text-[#2C352D] hover:text-[#D97757] py-1">{s.label}</a>
            ))}
          </nav>
          <a href="#top" className="mt-6 inline-flex items-center gap-1 text-xs text-[#5F6C60] hover:text-[#D97757]"><ArrowUp className="w-3 h-3" /> Back to top</a>
        </aside>

        {/* Body */}
        <article className="min-w-0">
          <H2 id="overview">1. What Massage Savings is</H2>
          <P>A <strong>flat-fee marketplace for massage deals</strong>. Therapists list discounted offers; customers contact them directly to book.</P>
          <P><strong>The platform's revenue model:</strong></P>
          <Table headers={["Source", "Amount", "Frequency"]} rows={[
            ["Individual subscription", "$49.99 per deal", "Monthly"],
            ["Business subscription", "$99.99 per deal", "Monthly"],
            ["Featured deal (pin-to-top)", "$19 per deal", "One-time, per purchase"],
          ]}/>
          <P><strong>The platform NEVER takes a cut</strong> of money customers pay providers for massages.</P>

          <H3>The 3 user roles</H3>
          <Table headers={["Role", "Pays platform?", "What they do"]} rows={[
            ["Customer", "Never", "Browses deals, contacts providers, leaves reviews"],
            ["Individual therapist", "$49.99/mo/deal", "Lists deals, takes bookings off-platform"],
            ["Massage business", "$99.99/mo/deal", "Same as individual, higher tier"],
            ["Admin (you)", "N/A", "Moderates everything via /admin"],
          ]}/>

          <H2 id="quick-start">2. Owner Quick Start</H2>
          <H3>First login</H3>
          <UL>
            <li>Go to <Code>https://massagesavings.com/login</Code></li>
            <li>Email: <Code>admin@massagesavings.com</Code></li>
            <li>Password: <Code>Admin123!</Code></li>
            <li>You'll auto-redirect to <Code>/admin</Code></li>
          </UL>

          <Note><strong>⚠️ Change your password immediately.</strong> The default is in source code. Use <Code>/forgot-password</Code> with the admin email → click the dev-mode link → set a strong new password.</Note>

          <H3>First-week checklist</H3>
          <UL>
            <li>Change admin password</li>
            <li>Set up email forwarding for hello@/legal@/privacy@ (ImprovMX free)</li>
            <li>Disable demo data: <Code>SEED_DEMO_DATA=false</Code> in prod env</li>
            <li>Hide reset tokens: <Code>EXPOSE_RESET_TOKEN=false</Code> in prod env</li>
            <li>Get live Stripe API key + create webhook at <Code>/api/webhook/stripe</Code></li>
            <li>Get Resend API key + verify massagesavings.com domain</li>
            <li>Redeploy with the new env vars</li>
          </UL>

          <H2 id="admin-console">3. The Admin Console</H2>
          <P>Located at <Code>/admin</Code>. Requires admin login.</P>

          <H3>Header KPIs</H3>
          <Table headers={["Card", "Shows"]} rows={[
            ["Pending review", "Deals waiting for you to approve/reject"],
            ["Trial deals", "Approved deals in their free 3-day trial"],
            ["Active subs", "Paid deals visible to customers"],
            ["Last 30d revenue", "Total subscription + Featured payments via Stripe"],
          ]}/>

          <H3>Tab: Pending</H3>
          <P>Your <strong>most important daily task</strong>. For each pending deal:</P>
          <UL>
            <li><strong>Approve</strong> → 3-day trial starts; owner notified in-app</li>
            <li><strong>Reject</strong> → dialog appears; enter a clear reason. Owner sees it.</li>
            <li><strong>Suspend</strong> → used for policy violations after approval</li>
          </UL>

          <H3>Tab: All deals</H3>
          <P>Every deal in the system. Buttons per row:</P>
          <UL>
            <li><strong>Approve / Reject</strong> — only on pending or rejected deals</li>
            <li><strong>Feature 7d</strong> — admin grant, no charge</li>
            <li><strong>Suspend / Unsuspend</strong> — toggle public visibility</li>
            <li><strong>Free forever</strong> — grants permanent free status (great for your own deals or partners)</li>
            <li><strong>Revoke free</strong> — removes Free forever; deal needs subscription to relist</li>
            <li><strong>Send message</strong> — in-app notification to owner</li>
          </UL>

          <H3>Tab: Users</H3>
          <P>Suspend, unsuspend, or send messages to any provider account.</P>

          <H3>Tab: Transactions</H3>
          <P>Every Stripe payment. Click <strong>Refund</strong> to issue a real refund via Stripe API (5–10 business days to return to customer's card).</P>

          <H3>Tab: Contact</H3>
          <P>Messages from the public <Code>/contact</Code> form. Click <strong>Reply</strong> to open your email client, or <strong>Resolve</strong> to mark handled.</P>

          <H3>CSV exports</H3>
          <P>Small "Export CSV" link in each tab — use for bookkeeping or outreach.</P>

          <H2 id="daily">4. Daily operations (~10 min)</H2>
          <UL>
            <li><strong>Pending tab</strong> — approve or reject new deals (target: within 24h)</li>
            <li><strong>Contact tab</strong> — reply to or resolve new messages</li>
            <li><strong>Dispatch queue</strong> (<Code>/admin/dispatch</Code>) — forward leads to providers</li>
            <li><strong>Last 30d revenue</strong> spot-check</li>
          </UL>

          <H3>Approval guidelines</H3>
          <P><strong>Approve when:</strong> descriptive title, 2+ sentence description, real discount, valid location, legitimate contact info, appropriate image.</P>
          <P><strong>Reject when:</strong> spammy/keyword-stuffed, misleading prices, adult/non-massage services, missing critical info. Always provide a polite reason.</P>

          <H2 id="weekly">5. Weekly & monthly tasks</H2>
          <H3>Weekly</H3>
          <UL>
            <li>Review Suspended deals — unsuspend or remove?</li>
            <li>Scan public reviews — flag any abusive content</li>
            <li>Spot-check Featured deals are still active</li>
          </UL>
          <H3>Monthly</H3>
          <UL>
            <li>Export Transactions CSV for accounting</li>
            <li>Outreach to trial deals that didn't convert</li>
            <li>Check Stripe Dashboard for disputes/failed payments</li>
            <li>Consider running a Featured promotion</li>
          </UL>

          <H2 id="therapists">6. How a therapist uses the platform</H2>
          <UL>
            <li><strong>Sign up</strong> → <Code>/register</Code> → Individual or Business → email + password</li>
            <li><strong>New deal</strong> → <Code>/dashboard/new</Code> → fill form, upload image, choose contact methods</li>
            <li><strong>Wait for approval</strong> → within 24h</li>
            <li><strong>Free 3-day trial</strong> begins automatically once approved</li>
            <li><strong>Subscribe</strong> → "Subscribe to relist" → Stripe Checkout → live for 30 days</li>
            <li><strong>Renew</strong> → click subscribe again before the 30-day window ends</li>
            <li><strong>Feature</strong> (optional) → $19 one-time for 7-day pin-to-top</li>
          </UL>

          <H2 id="customers">7. How a customer uses the platform</H2>
          <UL>
            <li>Land on home → click <strong>Browse Deals</strong></li>
            <li>Filter by ZIP, city, category, price</li>
            <li>Open a deal → use phone, email, or booking URL to contact</li>
            <li>Provider confirms appointment; customer pays provider directly</li>
            <li>After session, log in and leave a star rating + review</li>
          </UL>
          <Note><strong>Customers never pay Massage Savings.</strong> Customer accounts are 100% free.</Note>

          <H2 id="money">8. Money & payments (Stripe)</H2>
          <H3>Stripe setup for production</H3>
          <UL>
            <li>Get your live key at <Code>https://dashboard.stripe.com/apikeys</Code> (starts with <Code>sk_live_</Code>)</li>
            <li>Replace <Code>STRIPE_API_KEY</Code> in production env vars</li>
            <li>Create webhook at <Code>https://dashboard.stripe.com/webhooks</Code>:
              <UL>
                <li>Endpoint: <Code>https://massagesavings.com/api/webhook/stripe</Code></li>
                <li>Event: <Code>checkout.session.completed</Code></li>
                <li>Save the signing secret (<Code>whsec_...</Code>) for future webhook signature verification</li>
              </UL>
            </li>
            <li>Redeploy</li>
          </UL>
          <H3>Refunds</H3>
          <P>Admin → Transactions → click <strong>Refund</strong>. Real Stripe refund issued; customer credited in 5–10 business days.</P>
          <H3>Failed renewals</H3>
          <P>If a 30-day window expires unpaid, the deal disappears from public listings and the owner sees "Subscribe to relist" — they can re-subscribe anytime.</P>

          <H2 id="email">9. Email & notifications</H2>
          <H3>Current state</H3>
          <P>All notifications are <strong>in-app messages</strong> (owners see them in their dashboard). No email is sent yet — until Resend is wired.</P>
          <H3>ImprovMX setup (free, do this now)</H3>
          <UL>
            <li>Go to <Code>https://improvmx.com</Code> → add <Code>massagesavings.com</Code></li>
            <li>Add the 2 MX records at your registrar (5–10 min DNS propagation)</li>
            <li>Map <Code>hello@</Code>, <Code>legal@</Code>, <Code>privacy@</Code>, <Code>admin@</Code> → your real Gmail/Outlook</li>
            <li>People emailing those addresses now reach your inbox</li>
          </UL>
          <H3>Resend (future, sends FROM your domain)</H3>
          <P>Once you provide <Code>RESEND_API_KEY</Code> + verified from-address, we'll send: deal-approved, deal-rejected, trial-expiring (24h before), password reset, new review notifications.</P>

          <H2 id="glossary">10. Glossary</H2>
          <Table headers={["Term", "Meaning"]} rows={[
            ["Pending review", "Submitted, awaiting admin approval"],
            ["Trial", "Approved, live free for 3 days"],
            ["Active", "Subscription paid, live for 30 days"],
            ["Trial expired", "Trial window ended; subscribe to relist"],
            ["Expired", "Paid window ended; subscribe to relist"],
            ["Rejected", "Admin declined; owner can edit and resubmit"],
            ["Suspended", "Admin hid from public view"],
            ["Permanently free", "Special status; deal stays live without subscription"],
            ["Featured", "Pinned to top of search results (7 days)"],
            ["Redemption method", "How customer contacts provider: phone, email, or URL"],
            ["Dispatch request", "Customer-submitted lead via /need-therapist form"],
            ["Contact message", "Submission from public /contact form"],
          ]}/>

          <H2 id="troubleshoot">11. Troubleshooting</H2>
          <H3>I can't log in as admin</H3>
          <UL>
            <li>Verify email is <Code>admin@massagesavings.com</Code></li>
            <li>Forgot password? Use <Code>/forgot-password</Code> → dev-mode link still works until Resend is wired</li>
            <li>Locked out completely? Email Emergent support with your job ID</li>
          </UL>
          <H3>A deal disappeared from public listings</H3>
          <UL>
            <li>Check status in Admin → All deals</li>
            <li>Trial/active expired? Owner needs to subscribe</li>
            <li>Suspended? Unsuspend if appropriate</li>
            <li>Still in pending_review? Approve it</li>
          </UL>
          <H3>Stripe checkout returns an error</H3>
          <UL>
            <li>Verify <Code>STRIPE_API_KEY</Code> env matches your live/test key</li>
            <li>Check Stripe Dashboard → Logs for the exact error</li>
          </UL>
          <H3>Image upload fails</H3>
          <UL>
            <li>File must be JPEG/PNG/WEBP and under 5MB</li>
            <li>500 error? Object storage unreachable — check backend logs and <Code>EMERGENT_LLM_KEY</Code></li>
          </UL>
          <H3>Production looks different from preview</H3>
          <P>Production reflects the last deploy. After any code change, redeploy. Env-var changes are set in Emergent's deploy panel, not in the repo's <Code>.env</Code>.</P>

          <H2 id="env">12. Production environment reference</H2>
          <H3>Required env vars</H3>
          <div className="mt-3 bg-[#F9F7F3] border border-[#E8E2D6] rounded-2xl p-4 font-mono text-xs text-[#2C352D] whitespace-pre overflow-x-auto">
{`MONGO_URL=<auto-set by Emergent>
DB_NAME=<auto-set by Emergent>
JWT_SECRET=<generated, keep secret>
ADMIN_EMAIL=admin@massagesavings.com
ADMIN_PASSWORD=<your strong password>
STRIPE_API_KEY=sk_live_...
EMERGENT_LLM_KEY=sk-emergent-...
CORS_ORIGINS=https://massagesavings.com,https://www.massagesavings.com
EXPOSE_RESET_TOKEN=false   # set false in prod
SEED_DEMO_DATA=false       # set false in prod
RESEND_API_KEY=re_...      # future
RESEND_FROM_EMAIL=Massage Savings <noreply@massagesavings.com>`}
          </div>
          <H3>Tech stack</H3>
          <UL>
            <li><strong>Frontend:</strong> React, TailwindCSS, Shadcn UI, React Router</li>
            <li><strong>Backend:</strong> FastAPI, motor (async MongoDB), PyJWT, bcrypt, stripe, emergentintegrations</li>
            <li><strong>DB:</strong> MongoDB</li>
            <li><strong>Hosting:</strong> Emergent (Kubernetes)</li>
            <li><strong>Payments:</strong> Stripe</li>
            <li><strong>Object Storage:</strong> Emergent (via EMERGENT_LLM_KEY)</li>
            <li><strong>Email:</strong> Resend (not yet wired)</li>
          </UL>

          <Note>
            <strong>Full reference manual</strong> with every API endpoint, disaster recovery steps, and edge cases lives at <Code>/app/OPERATIONS_MANUAL.md</Code> in your code repo. This in-app version covers the essentials.
          </Note>

          <div className="mt-16 mb-8 text-center print:hidden">
            <Link to="/admin"><Button className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white h-12 px-8">Back to Admin Console</Button></Link>
          </div>
        </article>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.6in; }
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
